import { Box, Button, Paper, Grid2 as Grid, Select, MenuItem, FormControl, InputLabel } from "@mui/material"
import Stack from "@mui/material/Stack"
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid"
import { useMeasure } from "@uidotdev/usehooks"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import CustomToolbar from "../components/CustomToolbar"
import DialogForm from "../components/DialogForm"
import Layout from "../components/Layout"
import { PageTitle } from "../components/PageTitle"
import Paginator from "../components/Paginator"
import { useQsFilters } from "../hooks/filters"
import { Formato } from "../types"
import { Theaters } from "@mui/icons-material"
import { useSnackbarMessage } from "../hooks/snackbar"
import DialogConfirm from "../components/DialogConfirm"
import FormatoForm from "../components/forms/FormatoForm"
import { useFormatiList, useFormatoCreate, useFormatoDelete, useFormatoUpdate } from "../hooks/formati"
import useUserCanWriteAndDelete from "../hooks/permissions"
import IconButtonDelete from "../components/IconButtonDelete"
import IconButtonEdit from "../components/IconButtonEdit"
import { PAGE_SIZE_OPTIONS, usePageSize } from "../hooks/pageSize"

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get("page") ?? 1),
  search: params.get("search") ?? "",
  ordering: params.get("ordering") ?? "nome",
  unita_misura: params.get("unita_misura") ?? "",
})

export default function Formati() {
  const pageSize = usePageSize()
  const { filters, setFilters, setFiltersDebounced, uiFilters } = useQsFilters(initFilters)
  const { t } = useTranslation()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()

  const columns: GridColDef[] = useMemo(() => {
    let cols: GridColDef[] = []
    cols = [
      {
        field: "nome",
        headerName: t("formato.nome"),
        flex: 1,
      },
      {
        field: "unita_misura",
        headerName: t("formato.unita_misura"),
        flex: 1,
      },
      {
        field: "segnatura",
        headerName: t("formato.segnatura"),
        minWidth: 160,
      },
      {
        field: "frames_metro",
        headerName: t("formato.frames_metro"),
        flex: 1,
      },
    ]
    if (userCanWriteAndDelete) {
      cols.push({
        field: "id",
        headerName: t("general.azioni"),
        flex: 1,
        display: "flex",
        align: "right",
        headerAlign: "right",
        hideSortIcons: true,
        sortable: false,
        renderCell: (params) =>
          userCanWriteAndDelete && (
            <>
              <IconButtonEdit onClick={() => editModalActions.open(params.row)} />
              <IconButtonDelete disabled={!params.row.can_delete} onClick={() => deleteModalActions.open(params.row)} />
            </>
          ),
      })
    }
    return cols
  }, [t, userCanWriteAndDelete])

  const { data, isLoading } = useFormatiList({
    page: String(filters.page),
    page_size: String(pageSize),
    search: filters.search,
    ordering: filters.ordering,
    unita_misura: filters.unita_misura,
  })

  const { mutateAsync: create } = useFormatoCreate()
  const { mutateAsync: update } = useFormatoUpdate()
  const { mutateAsync: remove } = useFormatoDelete()

  const numPages = useMemo(() => {
    if (!data) return 1
    return Math.ceil(data.count / pageSize)
  }, [data, pageSize])

  const [ref, { height }] = useMeasure()
  const [toolbarRef, { height: toolbarHeight }] = useMeasure()
  const tableHeight = useMemo(() => {
    if (!height || !toolbarHeight) return
    return height - toolbarHeight
  }, [height, toolbarHeight])

  const [createModalState, createModalActions] = useModalTrigger()
  const [editModalState, editModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()

  const showMessage = useSnackbarMessage()

  const filtersCount = useMemo(() => {
    let count = 0
    if (filters.unita_misura) count++

    return count
  }, [filters.unita_misura])

  return (
    <Layout padding>
      <Stack sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <PageTitle
            title={t("formato.titolo_lista")}
            breadcrumbs={[
              {
                label: t("general.formati"),
                iconType: Theaters,
              },
            ]}
          />
          {userCanWriteAndDelete && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                createModalActions.open()
              }}
            >
              {t("formato.cta_aggiungi")}
            </Button>
          )}
        </Box>
        <Box flexGrow={1}>
          <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <CustomToolbar
              count={data?.count}
              search={uiFilters.search}
              setSearch={(search) => setFiltersDebounced({ ...filters, search, page: 1 })}
              clearFilters={() => setFilters({ search: "", page: 1, unita_misura: "" })}
              filtersCount={filtersCount}
              ref={toolbarRef}
            >
              <Grid container spacing={2}>
                <Grid size={3}>
                  <FormControl size="small" fullWidth margin="dense" variant="outlined">
                    <InputLabel>{t("formato.unita_misura")}</InputLabel>
                    <Select
                      fullWidth
                      value={filters.unita_misura}
                      onChange={(e) => {
                        setFilters({ unita_misura: e.target.value, page: 1 })
                      }}
                      size="small"
                      variant="outlined"
                      label={t("formato.unita_misura")}
                      margin="dense"
                    >
                      <MenuItem value={""}>{t("general.tutti")}</MenuItem>
                      <MenuItem value={"metri"}>{t("formato.enum_unita_misura.metri")}</MenuItem>
                      <MenuItem value={"minuti"}>{t("formato.enum_unita_misura.minuti")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CustomToolbar>
            <div
              style={{
                flexGrow: 1,
                overflow: "scroll",
                height: tableHeight ?? undefined,
              }}
              ref={ref}
            >
              {height && (
                <DataGrid
                  disableColumnMenu
                  disableColumnResize
                  // density="compact"
                  pageSizeOptions={PAGE_SIZE_OPTIONS}
                  paginationModel={{ page: 0, pageSize }}
                  sx={{
                    padding: 2,
                    "&.MuiDataGrid-root": {
                      border: "none",
                    },
                  }}
                  localeText={{
                    noRowsLabel: t("general.nessun_dato_disponibile"),
                  }}
                  loading={isLoading}
                  slots={{
                    footer: () => (
                      <Paginator
                        numPages={numPages}
                        page={filters.page}
                        setPage={(page) =>
                          setFilters({
                            ...filters,
                            page,
                          })
                        }
                      />
                    ),
                    toolbar: null,
                  }}
                  rows={data?.results ?? ([] as GridRowsProp)}
                  columns={columns}
                  onSortModelChange={(model) => {
                    if (model.length > 0) {
                      const sortSign = model[0].sort === "asc" ? "" : "-"
                      const newOrdering = `${sortSign}${model[0].field}`
                      setFilters({ ordering: newOrdering, page: 1 })
                    }
                  }}
                />
              )}
            </div>
          </Paper>
        </Box>
      </Stack>

      <DialogForm<Formato>
        open={createModalState.isOpen}
        handleClose={createModalActions.close}
        title={t("formato.cta_aggiungi")}
        FormComponent={FormatoForm}
        save={(values: Formato) => {
          return create(values).then(() => {
            createModalActions.close()
          })
        }}
      />
      <DialogForm<Formato>
        open={editModalState.isOpen}
        handleClose={editModalActions.close}
        title={t("formato.cta_modifica")}
        FormComponent={FormatoForm}
        save={(values: Formato) => {
          return update(values).then(() => {
            editModalActions.close()
            showMessage(t("formato.esito_modifica_ok"))
          })
        }}
        item={editModalState.value}
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("formato.cta_elimina")}
        subtitle={
          <span>
            {t("formato.cta_subtitle_elimina")} <b>{deleteModalState.value?.nome}</b> ?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("formato.esito_elimina_ok"))
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />
    </Layout>
  )
}
